import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { schritteFuer, type Seite, type TourRechte } from '../../lib/touren';
import { useEinfuehrungStore } from '../../stores/einfuehrungStore';
import './EinfuehrungsTour.css';

const ABSTAND = 14;   // Luft zwischen Ziel und Sprechblase
const RAND = 12;      // Mindestabstand zum Fensterrand
const KOPFZEILE = 64; // Hoehe der klebenden Kopfzeile
const WARTEN_MS = 2500; // So lange wird auf ein noch ladendes Ziel gewartet

type Rechteck = { top: number; left: number; width: number; height: number };

/** Ziel ins Bild holen. Von Hand statt scrollIntoView, weil die Kopfzeile klebt. */
function zielInsBildScrollen(el: Element) {
  const r = el.getBoundingClientRect();
  const hoehe = window.innerHeight;
  if (r.top >= KOPFZEILE + RAND && r.bottom <= hoehe - RAND) return;

  const ziel =
    r.height > hoehe * 0.45
      ? window.scrollY + r.top - KOPFZEILE - RAND
      : window.scrollY + r.top - (hoehe - r.height) / 2;
  window.scrollTo({ top: Math.max(0, ziel), behavior: 'smooth' });
}

/** Ausschnitt des Scheinwerfers, mit etwas Luft und geklemmt ans Fenster. */
function scheinwerfer(r: Rechteck) {
  const luft = 6;
  const top = Math.max(0, r.top - luft);
  const left = Math.max(0, r.left - luft);
  return {
    top,
    left,
    width: Math.min(window.innerWidth - left, r.width + luft * 2),
    height: Math.min(window.innerHeight - top, r.height + luft * 2),
  };
}

/** Sprechblase platzieren: Wunschseiten der Reihe nach durchprobieren. */
function kartePlatzieren(r: Rechteck, karte: { w: number; h: number }, seiten: Seite[]) {
  const fw = window.innerWidth;
  const fh = window.innerHeight;
  const mittigX = Math.min(Math.max(RAND, r.left + r.width / 2 - karte.w / 2), fw - karte.w - RAND);
  const mittigY = Math.min(Math.max(RAND, r.top + r.height / 2 - karte.h / 2), fh - karte.h - RAND);

  for (const seite of seiten) {
    if (seite === 'unten' && r.top + r.height + ABSTAND + karte.h <= fh - RAND)
      return { top: r.top + r.height + ABSTAND, left: mittigX };
    if (seite === 'oben' && r.top - ABSTAND - karte.h >= RAND)
      return { top: r.top - ABSTAND - karte.h, left: mittigX };
    if (seite === 'rechts' && r.left + r.width + ABSTAND + karte.w <= fw - RAND)
      return { top: mittigY, left: r.left + r.width + ABSTAND };
    if (seite === 'links' && r.left - ABSTAND - karte.w >= RAND)
      return { top: mittigY, left: r.left - ABSTAND - karte.w };
  }

  // Nichts passt: in die größere freie Haelfte unter oder über dem Ziel.
  const platzUnten = fh - (r.top + r.height);
  return platzUnten >= r.top
    ? { top: Math.min(r.top + r.height + ABSTAND, fh - karte.h - RAND), left: mittigX }
    : { top: Math.max(RAND, r.top - ABSTAND - karte.h), left: mittigX };
}

type Props = { rechte: TourRechte };

export function EinfuehrungsTour({ rechte }: Props) {
  const { tourId, schritt, springen, weiter, zurueck, beenden } = useEinfuehrungStore();
  const [rect, setRect] = useState<Rechteck | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const karteRef = useRef<HTMLDivElement>(null);

  const liste = useMemo(
    () => (tourId ? schritteFuer(tourId, rechte) : []),
    [tourId, rechte],
  );
  const aktuell = liste[Math.min(schritt, Math.max(0, liste.length - 1))];

  // Schritte mit Adresse auf derselben Seite, aber anderem Reiter
  // (?reiter=…): vorher umschalten. Den Seitenwechsel des Rundgangs erledigt
  // Einfuehrung beim Start. Ersetzt den Verlaufseintrag, damit „Zurück" im
  // Browser nicht jeden Schritt abläuft.
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const zielSeite = tourId ? aktuell?.seite : undefined;
  useEffect(() => {
    if (!zielSeite) return;
    const [pfad, abfrage = ''] = zielSeite.split('?');
    if (pathname !== pfad || !abfrage) return;
    const jetzt = new URLSearchParams(search);
    const passt = [...new URLSearchParams(abfrage)].every(([k, v]) => jetzt.get(k) === v);
    if (!passt) navigate(zielSeite, { replace: true });
  }, [zielSeite, pathname, search, navigate]);

  // Ziel suchen, messen und bei Scrollen/Größenänderung nachfuehren.
  useEffect(() => {
    if (!aktuell) return;
    // Sofort löschen, sonst umrahmt der Scheinwerfer noch das alte Ziel.
    setRect(null);
    setPos(null);
    if (!aktuell.ziel) return;

    let abgebrochen = false;
    let aufraeumen: (() => void) | null = null;
    const start = performance.now();

    const messen = (el: Element) => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    const anbinden = (el: Element) => {
      zielInsBildScrollen(el);
      messen(el);

      // Waehrend des weichen Scrollens nachfuehren, bis es zur Ruhe kommt.
      let ruhig = 0;
      let letzte = el.getBoundingClientRect().top;
      const beginn = performance.now();
      const nachfuehren = () => {
        if (abgebrochen) return;
        const jetzt = el.getBoundingClientRect().top;
        ruhig = Math.abs(jetzt - letzte) < 0.5 ? ruhig + 1 : 0;
        letzte = jetzt;
        messen(el);
        if (ruhig < 4 && performance.now() - beginn < 1000) requestAnimationFrame(nachfuehren);
      };
      requestAnimationFrame(nachfuehren);

      const bei = () => messen(el);
      window.addEventListener('resize', bei);
      window.addEventListener('scroll', bei, true);
      const beobachter = new ResizeObserver(bei);
      beobachter.observe(el);
      aufraeumen = () => {
        window.removeEventListener('resize', bei);
        window.removeEventListener('scroll', bei, true);
        beobachter.disconnect();
      };
    };

    // Seiten laden nach — deshalb eine Weile auf das Ziel warten.
    const suchen = () => {
      if (abgebrochen) return;
      const el = document.querySelector(`[data-tour="${aktuell.ziel}"]`);
      if (el) return anbinden(el);
      if (performance.now() - start < WARTEN_MS) requestAnimationFrame(suchen);
    };
    suchen();

    return () => {
      abgebrochen = true;
      aufraeumen?.();
    };
  }, [aktuell]);

  // Sprechblase setzen, sobald ihre Größe feststeht.
  useLayoutEffect(() => {
    if (!aktuell) return;
    const k = karteRef.current;
    if (!k) return;
    if (!rect) { setPos(null); return; }
    setPos(kartePlatzieren(
      rect,
      { w: k.offsetWidth, h: k.offsetHeight },
      aktuell.bevorzugt ?? ['unten', 'oben', 'rechts', 'links'],
    ));
  }, [rect, aktuell]);

  const naechster = useCallback(() => weiter(liste.length), [weiter, liste.length]);

  useEffect(() => {
    if (!tourId) return;
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') naechster();
      else if (e.key === 'ArrowLeft') zurueck();
      else if (e.key === 'Escape') beenden();
    };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, [tourId, naechster, zurueck, beenden]);

  if (!tourId || !aktuell) return null;

  const Icon = aktuell.Icon;
  const letzter = schritt >= liste.length - 1;

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-labelledby="tour-titel">
      {rect ? (
        <div className="tour-scheinwerfer" style={scheinwerfer(rect)} />
      ) : (
        <div className="tour-dunkel" />
      )}

      <div
        ref={karteRef}
        className={`tour-karte${pos ? '' : ' tour-karte--mittig'}`}
        style={pos ? { top: pos.top, left: pos.left } : undefined}
      >
        <button type="button" className="tour-schliessen" onClick={beenden} title="Beenden" aria-label="Rundgang beenden">
          <X size={16} />
        </button>

        <div className="tour-kopf">
          <span className="tour-symbol" aria-hidden="true"><Icon size={18} /></span>
          <div>
            <div className="tour-zeile">
              Schritt {schritt + 1} von {liste.length}
            </div>
            <h3 className="tour-titel" id="tour-titel">{aktuell.titel}</h3>
          </div>
        </div>

        <p className="tour-text">{aktuell.text}</p>

        <div className="tour-punkte" role="tablist" aria-label="Fortschritt">
          {liste.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === schritt}
              aria-label={`Schritt ${i + 1}: ${s.titel}`}
              className={`tour-punkt${i === schritt ? ' tour-punkt--aktiv' : ''}${
                i < schritt ? ' tour-punkt--fertig' : ''
              }`}
              onClick={() => springen(i)}
            />
          ))}
        </div>

        <div className="tour-knoepfe">
          <button type="button" className="tour-btn tour-btn--leise" onClick={beenden}>
            Überspringen
          </button>
          <div className="tour-knoepfe-rechts">
            {schritt > 0 && (
              <button type="button" className="tour-btn tour-btn--zweit" onClick={zurueck}>
                Zurück
              </button>
            )}
            <button type="button" className="tour-btn tour-btn--haupt" onClick={naechster}>
              {letzter ? 'Fertig' : 'Weiter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
